import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useCallback, useMemo } from 'react'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMainComponentCache } from 'src/api/main-component'
import {
  invalidateScheduledMaintenanceCache,
  useGetPerformableScheduledMaintenances,
  useGetScheduledMaintenancesByCage,
  useGetScheduledMaintenancesByEquipment,
  usePerformScheduledMaintenances,
} from 'src/api/scheduled-maintenance'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { IScheduledMaintenance } from 'src/generated-types/scheduled-maintenance'
import { ShowDialogProps } from 'src/show-dialog'
import { getTranslatedText } from 'src/utils'
import { PerformMaintenanceForm } from './PerformMaintenanceForm'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { ScheduledMaintenanceFilterParams } from 'src/generated-types/scheduled-maintenance-filter'
import { ICagePosition } from 'src/generated-types'
import { getCageDisplayLabel, formatItemIdentification, getMooringName } from 'src/utils/function'

interface ScheduledMaintenancePerformDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  scheduledMaintenance?: IScheduledMaintenance
  mainComponentType?: IMainComponentType
  isPerformAll: boolean
  onlyDaily: boolean
  cagePosition?: ICagePosition | undefined
  filterIds?: ScheduledMaintenanceFilterParams
  nextScheduledMaintenanceByEquipment?: IScheduledMaintenance[]
  isEquipmentMaintenaceRowPerform: boolean
  isSinglePerform?: boolean
}

export const ScheduledMaintenancePerformDialog: React.FC<ScheduledMaintenancePerformDialogProps> = ({
  pop,
  customerId,
  localityId,
  scheduledMaintenance,
  mainComponentType,
  isPerformAll,
  onlyDaily,
  cagePosition,
  filterIds,
  nextScheduledMaintenanceByEquipment,
  isEquipmentMaintenaceRowPerform,
  isSinglePerform
}) => {
  const { t } = useTranslation(['common-api-message', 'maintenance'])

  const { data: ScheduledMaintenancesByEquipment } = useGetScheduledMaintenancesByEquipment(
    customerId,
    localityId,
    mainComponentType?.id as number,
    onlyDaily,
    filterIds,
    { enabled: !!mainComponentType }
  )

  const { data: ScheduledMaintenancesByCage } = useGetScheduledMaintenancesByCage(
    customerId,
    localityId,
    cagePosition?.id as EntityId,
    onlyDaily,
    filterIds,
    { enabled: !!cagePosition?.id }
  )

  const scheduledMaintenanceIds = useMemo(
    () =>
      cagePosition?.id
        ? ScheduledMaintenancesByCage?.map(sm => sm.id) ?? []
        : mainComponentType
          ? ScheduledMaintenancesByEquipment?.map(sm => sm.id) ?? []
          : isEquipmentMaintenaceRowPerform && nextScheduledMaintenanceByEquipment
            ? nextScheduledMaintenanceByEquipment.map(sm => sm.id)
            : scheduledMaintenance?.id
              ? [scheduledMaintenance.id]
              : [],
    [
      cagePosition?.id,
      ScheduledMaintenancesByCage,
      mainComponentType,
      ScheduledMaintenancesByEquipment,
      isEquipmentMaintenaceRowPerform,
      nextScheduledMaintenanceByEquipment,
      scheduledMaintenance?.id,
    ],
  )

  const { data: performableScheduledMaintenances } = useGetPerformableScheduledMaintenances(
    customerId,
    localityId,
    scheduledMaintenanceIds,
    { enabled: (isPerformAll || isEquipmentMaintenaceRowPerform) && scheduledMaintenanceIds.length > 0 },
  )

  const mainComponentSerialNumbers = useMemo((): string[] => {
    if (cagePosition?.id) {
      return Array.from(
        new Set(
          performableScheduledMaintenances?.map(sm =>
            formatItemIdentification({
              itemType: getTranslatedText(sm.mainComponentTypeName ?? ''),
              positionName: sm.mainComponent?.positionName,
              positionReference: sm.mainComponent?.reference,
              serialNumber: sm.serialNumber,
            }),
          ) ?? [],
        ),
      )
    }
    if (mainComponentType) {
      return Array.from(
        new Set(
          performableScheduledMaintenances?.map(sm => sm.serialNumber).filter((serial): serial is string => !!serial) ??
            [],
        ),
      )
    }
    if (isEquipmentMaintenaceRowPerform && nextScheduledMaintenanceByEquipment) {
      return Array.from(
        new Set(
          nextScheduledMaintenanceByEquipment.map(sm =>
            formatItemIdentification({
              itemType: getTranslatedText(sm.mainComponentTypeName ?? ''),
              serialNumber: sm.serialNumber,
            }),
          ),
        ),
      )
    }
    return scheduledMaintenance?.maintenance?.name ? [scheduledMaintenance.maintenance.name] : []
  }, [
      cagePosition?.id,
      performableScheduledMaintenances,
      mainComponentType,
      isEquipmentMaintenaceRowPerform,
      nextScheduledMaintenanceByEquipment,
      scheduledMaintenance?.maintenance?.name,
  ])

  const mutation = usePerformScheduledMaintenances(customerId, localityId)
  const onSubmit = useCallback(
    async form => {
      form.scheduledMaintenanceIds = scheduledMaintenanceIds
      form.isSinglePerform = isSinglePerform
      await mutation.mutateAsync(form, {
        onSuccess: () => {
          invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId, scheduledMaintenance?.cagePositionId as EntityId, scheduledMaintenance?.mainComponentTypeId as EntityId)
          invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(customerId, localityId)
          invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
          invalidateMainComponentCache.getMaintenancePerformHistory(customerId)
          invalidateScheduledMaintenanceCache.getEquipmentPerformedMaintenances(customerId, localityId)
          invalidateScheduledMaintenanceCache.getLocalityPerformedMaintenances(customerId, localityId)
          invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId)
          invalidationActivityLogCache.getCageActivityLogs()
          invalidationActivityLogCache.getCageMaintenanceLogs()
          toast.success(t('performed-successfully', { ns: 'maintenance' }))
          pop()
        },
        onError: () => {
          toast.error(t('unExpectedError', { ns: 'common-api-message' }))
        },
      })
    },
    [scheduledMaintenanceIds, isSinglePerform, mutation, customerId, localityId, scheduledMaintenance?.cagePositionId, scheduledMaintenance?.mainComponentTypeId, t, pop],
  )

  const maintenanceName = useMemo(() => {
    if (mainComponentType) {
      return getTranslatedText(mainComponentType.name)
    }
    return scheduledMaintenance?.maintenance?.name ?? ''
  }, [mainComponentType, scheduledMaintenance?.maintenance?.name])

  const cagePositionDisplayName = useMemo(() => {
    if (!cagePosition) return ''
    return `${getCageDisplayLabel(cagePosition.name, cagePosition.reference)} (${getMooringName(cagePosition.mooringName)})`
  }, [cagePosition])

  return (
    <PerformMaintenanceForm
      onSubmit={onSubmit}
      onCancel={pop}
      maintenanceName={maintenanceName}
      note={mainComponentType ? '' : scheduledMaintenance?.maintenance?.note}
      mainComponentSerialNumbers={mainComponentSerialNumbers}
      isPerformAll={isPerformAll}
      file={scheduledMaintenance?.maintenance?.file}
      performableMaintenances={performableScheduledMaintenances}
      isCagePerform={!!cagePosition?.id}
      cagePositionName={cagePositionDisplayName}
      isEquipmentMaintenaceRowPerform={isEquipmentMaintenaceRowPerform}
      isSubmitting={mutation.isLoading}
    />
  )
}
